import React, { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import type { FileRejection } from 'react-dropzone'
import { FontAwesomeIcon, Text, Variables, Props } from '@intellihr/ui-components'
import { useBrowserBreakpoints } from 'src/services/reducers/browser/hooks'
import { useScope } from 'src/services/i18n/LocalizationProvider'
import {
  StyledCallToAction,
  StyledDropzone,
  StyledOverlay,
  StyledTextWrapper,
  StyledWrapper,
  StyledIconWrapper,
} from './style'

interface IDocumentDropzoneProps {
  inputRef: React.MutableRefObject<React.RefObject<HTMLInputElement> | null>
  onDropFilesSuccess: (files: File[]) => void
  onDropFileError: (error: string) => void
  progressEvent?: ProgressEvent | null
  hasNoFiles: boolean
  showCallToActionWhenNoFiles?: boolean
}

const fileSizeLimit = 100 * 1000000

const DocumentDropzone: React.FC<React.PropsWithChildren<IDocumentDropzoneProps>> = ({
  inputRef: inputRefParent,
  onDropFilesSuccess,
  onDropFileError,
  children,
  hasNoFiles,
  showCallToActionWhenNoFiles = true,
}) => {
  const t = useScope('common:components.DocumentDropzone')
  const { mobile } = useBrowserBreakpoints()
  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        const filesTooLarge = rejected.filter((fileRejection) => fileRejection.file.size > fileSizeLimit)

        if (filesTooLarge.length > 0) {
          const filenames = filesTooLarge.map((fileRejection) => fileRejection.file.name).join(', ')
          return onDropFileError(
            t('documentTooLargeErrorMessage', {
              count: filesTooLarge.length,
              filenames,
            }),
          )
        }

        return onDropFileError(t('invalidDocumentDropped'))
      }

      return onDropFilesSuccess(accepted)
    },
    [onDropFilesSuccess, onDropFileError, t],
  )

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    noClick: !hasNoFiles,
    noKeyboard: !hasNoFiles,
    noDrag: mobile,
    maxSize: fileSizeLimit,
  })

  // react-dropzone requires that we use the one from `useDropzone` - we'll just wrap it so the parent can access
  inputRefParent.current = inputRef

  return (
    <StyledWrapper
      {...getRootProps()}
      showCursor={hasNoFiles}
    >
      <StyledDropzone
        isDragActive={isDragActive}
        hasNoFiles={hasNoFiles}
      >
        <input {...getInputProps()} />
        <StyledOverlay showOverlay={isDragActive} />
        {children}
      </StyledDropzone>
      {(isDragActive || (showCallToActionWhenNoFiles && hasNoFiles)) && !mobile && (
        <StyledCallToAction>
          <StyledIconWrapper shouldAnimate={isDragActive}>
            <FontAwesomeIcon
              type="duotone"
              icon="cloud-upload-alt"
              size="xxlarge"
              duotoneOpacity={{
                primaryOpacity: 1,
                secondaryOpacity: 1,
              }}
              duotoneColors={{
                secondaryColor: Variables.Color.i300,
                primaryColor: Variables.Color.n100,
              }}
            />
          </StyledIconWrapper>
          <StyledTextWrapper>
            <Text type={Props.TypographyType.Small}>{t('dropzoneCallToAction')}</Text>
          </StyledTextWrapper>
        </StyledCallToAction>
      )}
    </StyledWrapper>
  )
}

export { DocumentDropzone }
